// Pure derivations for the session home's LIVE Progress panel (CYBORG-794 step 4). Real
// data only — a todo checklist when the provider emits one, else the recent tool feed;
// NEVER a fabricated plan (the session home does not read work_session.plan). Kept pure +
// dependency-free (a type import from the agent stream + the pure activity curator) so the
// branching is unit-tested without a DOM; the route resolves the live entries and feeds
// them in.
import type { StreamEntry } from "./plugins/agents/state.svelte.js";
import { todoSummaryText, type CuratedActivity } from "./plugins/agents/activity-curator.js";
import { toolAction } from "./plugins/agents/tool-action-phrase.js";

// The STABLE agent-stream key a work session's timeline is rendered under — session-scoped,
// never session.agentId (see SessionWorkView's header note: a cold resume swaps in a fresh
// agentId and the reuse path shares one agent across sessions). ONE definition, imported by
// both the body that WRITES the stream (SessionWorkView) and the Progress rail that READS it
// — the string used to be inlined in the body only, and the rail read the raw agentId
// instead, which is why it said "No live activity yet." while the body showed dozens of tool
// calls: nothing ever hydrates the per-agent stream on this route (the durable page is
// fetched into the SESSION key), so the agent key held only whatever frames happened to
// arrive while the page stayed open — empty on any fresh load, and empty again after a
// workspace switch clears the streams.
const SESSION_STREAM_PREFIX = "session:";

export function sessionStreamKey(sessionId: string): string {
  return `${SESSION_STREAM_PREFIX}${sessionId}`;
}

// The inverse, so ONE module owns the prefix. A work-session key resolves to no agent
// binding, so an AGENT-scoped read of one can only fail — and a read that can only fail must
// not be re-issued on every reconnect (see timelineIdsToCatchUp).
export function isSessionStreamKey(streamId: string): boolean {
  return streamId.startsWith(SESSION_STREAM_PREFIX);
}

// The agent id whose LIVE TURN a work-session composer must read — and, therefore, the id
// its STOP must cancel. The composer keys its own client-side state (draft, queue, stream
// lookups) off `agent.agentId ?? cold-session:<workSessionId>`, and that COLD fallback is a
// key NOTHING ever writes turn frames to: `agent_stream` frames land under the REAL agentId
// and SessionWorkView mirrors them onto `session:<id>`, never onto `cold-session:<id>`. So a
// composer reading turn state off the cold key saw "idle" for a turn that was visibly
// running two inches above it, which is why a running work session showed NO stop control at
// all — the one escape hatch the user has was structurally unreachable exactly when the
// session had no roster-resolved agent (a session whose agentId isn't in workspaceState
// .agents: a mid-session promotion, a cloud client whose roster hasn't caught up).
//
// `sessionAgentId` is the work_sessions row's own agentId, read from the SAME live store the
// composer already reads `status` from — not a new prop and not a second source of truth, so
// the composer and the session body cannot drift apart (SessionWorkView's `isWorking` reads
// the live turn off this very id).
//
// Ranked live-agent FIRST so every WARM/DM caller is byte-identical: with a roster agent
// attached this returns exactly `agent.agentId`, and with no work session at all
// (`sessionAgentId` undefined) it returns the composer key unchanged.
export function sessionTurnAgentId(input: {
  liveAgentId?: string | null;
  sessionAgentId?: string | null;
  composerKey: string;
}): string {
  return input.liveAgentId || input.sessionAgentId || input.composerKey;
}

// The agent's live WORK line (typing detail, anchor view) — the newest entry that is the AGENT's own WORK (a
// tool call, a todo, reasoning, an error). SKIPS the prompt AND the reply:
//   - the prompt: a session injects its agent prompt (the "@-mentioned in #… Participants …
//     BEGIN UNTRUSTED CONTENT" context + the untrusted-content fence) as a `user_message`.
//     It must NEVER echo the incoming prompt / injected context / fence.
//   - the reply: the agent's chat text is ALREADY rendered verbatim in the work timeline;
//     surfacing it here too printed the last assistant message a second time
//     (Bug 5). This is WORK, so prose is skipped — it falls back to the last real
//     tool/todo/reasoning activity, and to null when the ONLY thing said is chat (→ the
//     honest empty state).
//
// Walks the RAW entries backward rather than the curated lines, because the wording of a
// tool row is exactly what was broken: curating first collapses the call into
// `[mcp__cyborg7__cyborg7_list_states] {"projectId":"tp_…"}` and there is no way back to the
// name and arguments the plain phrase is derived from. Everything else keeps the curator's
// vocabulary and shape (`CuratedActivity`, `[Label] text`), so `sessionTypingFromAgent` is
// unchanged. `getLastActivity` keeps its `[User]`/`[Assistant]`
// labeling for the agents pane / cybo activity surfaces — untouched here.
function curatedLine(label: string, text: string, kind: CuratedActivity["kind"]): CuratedActivity {
  const summary = text.trim();
  return { label, text: summary, line: summary ? `[${label}] ${summary}` : `[${label}]`, kind };
}

export function latestAgentActivity(entries: readonly StreamEntry[]): CuratedActivity | null {
  for (let i = entries.length - 1; i >= 0; i--) {
    const entry = entries[i];
    switch (entry.kind) {
      case "tool_call": {
        const action = toolAction(entry);
        return curatedLine(action.action, action.object, "Tool");
      }
      case "todo":
        return curatedLine("Tasks", todoSummaryText(entry.items), "Tasks");
      case "thinking":
        return curatedLine("Thought", entry.content, "Thought");
      case "error":
        return curatedLine("Error", entry.content, "Error");
      case "compaction":
        return curatedLine("Compacted", "", "Compacted");
      default:
        // text (the reply), user_message (the prompt), turn_boundary — none is work.
        continue;
    }
  }
  return null;
}

// The shape MessageList's `typing` prop wants (a superset — it also accepts a bare fromName).
export interface SessionTyping {
  fromName: string;
  activity?: string;
}

// The "<agent> is typing…" line for a work session's inline conversation (CYBORG-794), driven
// by the session's OWN agent turn — NOT the per-surface typing stores. Those stores
// (channelState / threadState / dmTypingState) only populate for the currently-OPEN
// channel/thread/DM surface, which the session route is NOT — so they never fired for a
// thread/channel-anchored session (the previous attempt's proven failure: only the
// workspace-scoped DM store ever lit up). A session is a conversation with ONE agent, and that
// agent's live stream is workspace-agnostic + always available in the session view (the SAME
// source the Progress panel reads), so its turn status is the truthful "is this agent working
// right now" signal for EVERY anchor kind. While the agent has an active turn (turnStatus
// "running") → "<cybo> is typing…" with its latest OWN activity as the detail; idle / no live
// agent → no typing.
//
// `activity` MUST be latestAgentActivity's result (kinds "User"/"Assistant" already excluded)
// so neither the injected agent prompt / untrusted-content fence nor the plain chat reply
// leaks into the typing detail; a chat-only or empty stream passes null here → a plain
// "<cybo> is typing…". MessageList runs cleanTypingActivity over the detail, so the raw
// curated summary passes straight through.
export function sessionTypingFromAgent(
  agentId: string | null,
  cyboName: string,
  turnStatus: string,
  activity: CuratedActivity | null,
): SessionTyping[] {
  if (!agentId || turnStatus !== "running") return [];
  const detail = activity?.text.trim();
  return detail ? [{ fromName: cyboName, activity: detail }] : [{ fromName: cyboName }];
}
